import React from "react";
import { useSelector } from "react-redux";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./Progress.styles";
import { StoreState } from "../../models/states/StoreState";
import { SurveyMode } from "../../models/enums/SurveyMode";
import { some, find } from "../../utils/lodash";
import { translate } from "../../models/utilities/translationUtilities";
import { useLanguage } from "../../hooks/useLanguage";
import { ThemeModel } from "../../models/ThemeModel";
import type { Theme } from "../../types/Theme";

interface Props {
    value: number;
    position?: "top" | "bottom";
    surveyTheme: ThemeModel;
    showOnlyProgressContainer?: boolean;
}

function progressTextContainerPosition(theme: Theme) {
    if (!theme.ProgressBarShowValue && !theme.ProgressBarLabelActive) {
        return "none";
    }
    if (theme.ProgressBarType === "floating") {
        return "floating";
    }
    return "bottom";
}

function progressTextPosition(theme: Theme) {
    if (!theme.ProgressBarShowValue) {
        return "none";
    }
    if (theme.ProgressBarType === "floating") {
        return "floating";
    }
    return "bottom";
}

function getProgressLabelStatus(theme: Theme) {
    return theme.ProgressBarLabelActive;
}

function getProgressBarShowValue(theme: Theme) {
    return theme.ProgressBarShowValue;
}
function progressBarPosition(theme: Theme) {
    return theme.ProgressBarType;
}

export default function Progress(props: Props) {
    const { value, position, surveyTheme, showOnlyProgressContainer } = props;
    const boundedValue = Math.max(Math.min(value, 100), 0);
    const remainderValue = 100 - boundedValue;
    const simulated = useSelector(
        (state: StoreState) =>
            process.env.NODE_ENV !== "development" && // Do not show simulated warning if we are in dev.
            state.interview.respondent &&
            state.interview.respondent.simulated,
    );
    const simulatedBannerVisible =
        useSelector((state: StoreState) => state.application.mode !== SurveyMode.Normal) &&
        simulated;

    const progressBarRef = React.useRef(null);
    const [progressBarWidth, setProgressBarWidth] = React.useState(0);
    const [hover, setHover] = React.useState(false);
    const [isMobilePreview, setIsMobilePreview] = React.useState(false);
    const [language] = useLanguage();
    const theme = useTheme<Theme>();

    const useModernTheme = Boolean(theme.UseModernTheme);
    const useModernBasic = useModernTheme && theme.ProgressBarType !== "floating";

    const progressLabelText = translate(surveyTheme, "progress", language);
    const isShowProgressLabel = getProgressLabelStatus(theme);
    const isShowPercentage = getProgressBarShowValue(theme);
    const showProgressBarContent = isShowProgressLabel || isShowPercentage;
    const classes = useStyles({
        theme,
        position,
        simulatedBannerVisible,
        isMobilePreview,
        showOnlyProgressContainer,
        showProgressBarContent,
    });
    const textPosition = progressTextPosition(theme);
    const progressBarContainerPosition = progressTextContainerPosition(theme);

    const onWindowSizeChanged = React.useCallback(() => {
        if (!progressBarRef || !progressBarRef.current) {
            return;
        }
        // Adjust progress bar position on mobile preview
        if (progressBarPosition(theme) === "floating") {
            const deviceFrame = progressBarRef.current.closest(".device-frame");
            if (deviceFrame) {
                if (!some(deviceFrame.childNodes, (child) => child === progressBarRef.current)) {
                    // Remove any other progress bar with same position
                    const child = find(
                        deviceFrame.childNodes,
                        (c: HTMLElement) => c.getAttribute("data-position") === position,
                    );
                    if (child) deviceFrame.removeChild(child);
                    // Move progress bar the its new position
                    deviceFrame.appendChild(progressBarRef.current);
                }
            }
        }
        setIsMobilePreview(progressBarRef.current.closest(".preview__mobile") != null);
        setProgressBarWidth(progressBarRef.current.getBoundingClientRect().width);
    }, [position, theme]);

    React.useLayoutEffect(() => {
        if (useModernBasic) {
            return undefined;
        }
        onWindowSizeChanged();
        window.addEventListener("resize", onWindowSizeChanged);
        return function cleanup() {
            window.removeEventListener("resize", onWindowSizeChanged);
        };
    }, [onWindowSizeChanged, useModernBasic]);

    if (useModernBasic) {
        return (
            <div
                className={classes.progress}
                onFocus={() => setHover(true)}
                onBlur={() => setHover(false)}
                onMouseOver={() => setHover(true)}
                onMouseOut={() => setHover(false)}
            >
                <div
                    className={classes.progressModern}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={boundedValue}
                    aria-label={progressLabelText}
                >
                    {!showOnlyProgressContainer && (
                        <div className={classes.progressModernTrack} data-position={position}>
                            <div
                                className={classes.progressModernFill}
                                style={{
                                    flexGrow: boundedValue,
                                    flexShrink: 0,
                                    flexBasis: 0,
                                }}
                            ></div>
                            <div
                                className={classes.progressModernRemainder}
                                style={{
                                    flexGrow: remainderValue,
                                    flexShrink: 0,
                                    flexBasis: 0,
                                }}
                            ></div>
                        </div>
                    )}
                    {(progressBarContainerPosition === "bottom" || showOnlyProgressContainer) &&
                        showProgressBarContent && (
                            <div className={classes.progressModernLabelRow}>
                                {isShowProgressLabel && (
                                    <div className={classes.progressModernLabel}>
                                        {progressLabelText}
                                    </div>
                                )}
                                {isShowPercentage && (
                                    <div className={classes.progressModernValue}>
                                        {`${Math.round(boundedValue)}%`}
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={classes.progress}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
        >
            {!showOnlyProgressContainer && (
                <div
                    ref={progressBarRef}
                    className={classes.bar}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={boundedValue}
                    data-position={position}
                    title={progressLabelText}
                    aria-label={!isShowProgressLabel ? progressLabelText : null}
                >
                    <div className={classes.fill} style={{ width: `${boundedValue}%` }}></div>
                </div>
            )}
            {(progressBarContainerPosition === "bottom" || showOnlyProgressContainer) && (
                <div className={classes.progressContainer}>
                    {isShowProgressLabel && (
                        <div className={classes.progressLabelValue}>{progressLabelText}</div>
                    )}
                    {isShowPercentage && (
                        <div className={classes.bottomValue}>{`${Math.round(boundedValue)} %`}</div>
                    )}
                </div>
            )}
            {textPosition === "floating" && (
                <div
                    className={classnames(
                        classes.floatingValue,
                        hover && classes.floatingValueHover,
                    )}
                    style={{
                        left: Math.min(
                            progressBarWidth - 50,
                            Math.max(50, (progressBarWidth * boundedValue) / 100),
                        ),
                    }}
                >
                    {`${Math.round(boundedValue)} %`}
                    <div className={classes.floatingValueArrowBorder} />
                    <div className={classes.floatingValueArrow} />
                </div>
            )}
        </div>
    );
}
